import { useTranslation } from 'react-i18next';
import { ShieldAlert } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import type { Deal } from '../service/types';
import { isDealRemoved } from '../utils/moderation';

/**
 * Calm, destructive-styled banner shown on the deal editor/detail when a deal
 * was removed by Digipop moderation. Read-only — there is deliberately no
 * merchant action; it only explains the takedown and (when present) the reason.
 */
export function DealModerationBanner({
  deal,
}: {
  deal: Pick<Deal, 'moderation_status' | 'moderation_reason'>;
}) {
  const { t } = useTranslation();
  if (!isDealRemoved(deal)) return null;

  const reason = deal.moderation_reason?.trim();

  return (
    <Alert variant="destructive" className="mb-4">
      <ShieldAlert aria-hidden />
      <AlertTitle>{t('deals.moderation.title')}</AlertTitle>
      <AlertDescription>
        <span>
          {reason ? t('deals.moderation.reason', { reason }) : t('deals.moderation.noReason')}
        </span>
        <span>{t('deals.moderation.hidden')}</span>
      </AlertDescription>
    </Alert>
  );
}

/**
 * Compact destructive badge for the deals list row, signalling at a glance that
 * a deal was removed by moderation. The full reason lives on the detail banner;
 * here it surfaces via the title tooltip when present.
 */
export function DealModerationBadge({
  deal,
}: {
  deal: Pick<Deal, 'moderation_status' | 'moderation_reason'>;
}) {
  const { t } = useTranslation();
  if (!isDealRemoved(deal)) return null;

  const reason = deal.moderation_reason?.trim();

  return (
    <Badge
      variant="destructive"
      className="gap-1"
      title={reason ? t('deals.moderation.reason', { reason }) : undefined}
    >
      <ShieldAlert aria-hidden className="size-3" />
      {t('deals.moderation.badge')}
    </Badge>
  );
}
